import { unwrapResult } from "@reduxjs/toolkit";
import { LoadingButton } from "@mui/lab";
import { Autocomplete, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Skeleton, TextField } from "@mui/material";
import { Localized } from "@fluent/react";
import { useCallback, useMemo, useState } from "react";
import { LoadUsers } from "../../../../app/AppDataLoader";
import { APIError, AppId, AppTextFieldHandler } from "../../../../app/appTypes";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { store } from "../../../../app/store";
import { ServerErrorMsg } from "../../../../app/ServerErrorMsg";
import { selectAllUsers, selectUserByName, User } from "../../../users/usersSlice";
import { createProjectTeamMember, ProjectTeamMember } from "../../projectTeamMembersSlice";

export interface ValidationProjectTeamMemberDialogProps {
	projectId: AppId;
	defaultUserName?: string;
	excludedUsers: Set<string>;
	onSave: (teamMember: ProjectTeamMember) => void;
	onCancel: () => void;
}

export const ValidationProjectTeamMemberDialog = ({ projectId, defaultUserName = "", excludedUsers, onSave, onCancel }: ValidationProjectTeamMemberDialogProps) => {
	const dispatch = useAppDispatch();
	const allUsers = useAppSelector(selectAllUsers);
	const [user, setUser] = useState<User | null>(() => selectUserByName(store.getState(), defaultUserName) ?? null);
	const [userRole, setUserRole] = useState<string>("");
	const [error, setError] = useState<APIError | null>(null);
	const [savingInProgress, setSavingInProgress] = useState(false);

	const availableUsers = useMemo(
		() => allUsers.filter((availableUser) => !excludedUsers.has(availableUser.userName) || availableUser.userName === defaultUserName),
		[allUsers, defaultUserName, excludedUsers]
	);

	const handleUserChange = useCallback((_, newUser) => {
		setUser(newUser);
	}, []);

	const handleUserRoleChange: AppTextFieldHandler = useCallback((e) => {
		setUserRole(e.target.value);
	}, []);

	const handleOkClick = useCallback(() => {
		if (user === null) {
			return;
		}

		setSavingInProgress(true);
		setError(null);
		dispatch(createProjectTeamMember({ user: user.userName, projectId, role: userRole }))
			.then(unwrapResult)
			.then((teamMember) => onSave(teamMember))
			.catch((err: APIError) => setError(err))
			.finally(() => setSavingInProgress(false));
	}, [dispatch, onSave, projectId, user, userRole]);

	return <>
		<Dialog open={true} fullWidth maxWidth="md">
			<DialogTitle>
				<Localized id="urs-add-team-member">
					<span>Nowy członek zespołu</span>
				</Localized>
			</DialogTitle>
			<DialogContent sx={{ padding: 2 }}>
				<LoadUsers component={<Skeleton />}>
					<Autocomplete
						sx={{ marginTop: 1 }}
						options={availableUsers}
						value={user}
						onChange={handleUserChange}
						getOptionLabel={({ firstname, surname }) => `${firstname} ${surname}`}
						renderInput={(params) => (
							<TextField
								{...params}
								helperText={user?.position ?? " "}
								label={<Localized id="username"><span>Nazwa użytkownika</span></Localized>}
							/>
						)}
					/>
				</LoadUsers>
				<TextField
					sx={{ marginTop: 1 }}
					value={userRole}
					fullWidth
					required={true}
					select
					label={<Localized id="urs-user-role"><span>Rola</span></Localized>}
					onChange={handleUserRoleChange}
				>
					<MenuItem value={"C"}><Localized id="project-role-collaborator"><span>Collaborator</span></Localized></MenuItem>
					<MenuItem value={"O"}><Localized id="project-role-observer"><span>Observer</span></Localized></MenuItem>
				</TextField>
				<ServerErrorMsg err={error} />
			</DialogContent>
			<DialogActions>
				<LoadingButton loading={savingInProgress} onClick={handleOkClick}>
					<Localized id="ok"><span>OK</span></Localized>
				</LoadingButton>
				<LoadingButton loading={savingInProgress} onClick={onCancel}>
					<Localized id="cancel"><span>Anuluj</span></Localized>
				</LoadingButton>
			</DialogActions>
		</Dialog>
	</>;
};
